import { useState, useRef, useEffect } from 'react'
import { Shield, BarChart3, FileSearch, Bell, ArrowRight, CheckCircle2, XCircle, Zap, FileText, AlertTriangle, Lock, Brain, DollarSign, X, Play, Target } from 'lucide-react'
import { PLANS, ENTERPRISE } from '../constants/plans'
import { trackEvent } from '../lib/analytics'

interface LandingPageProps {
  onGetStarted: () => void
  onViewDashboard: () => void
  onContact?: () => void
  onCheckout?: () => void
  onTerms?: () => void
  onPrivacy?: () => void
  onRefundPolicy?: () => void
  onCookiePolicy?: () => void
}

const DEMO_VIDEO_URL = 'https://media.auditguard.org/auditguard-demo.mp4'

const LandingPage = ({ onGetStarted, onViewDashboard, onContact, onCheckout, onTerms, onPrivacy, onRefundPolicy, onCookiePolicy }: LandingPageProps) => {
  const [showDemo, setShowDemo] = useState(false)
  const videoRef = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    if (!showDemo && videoRef.current) {
      videoRef.current.pause()
    }
  }, [showDemo])

  const features = [
    {
      icon: Shield,
      title: 'Multi-Framework Compliance',
      description: 'Analyze contracts against 11 frameworks including HIPAA, GDPR, UAE PDPL, Saudi PDPL, IFRS & more'
    },
    {
      icon: AlertTriangle,
      title: 'Violation Detection',
      description: 'AI identifies specific clause violations with penalty context and risk severity'
    },
    {
      icon: FileText,
      title: 'Auto-Remediation',
      description: 'Generate suggested replacement language aligned with cited regulatory requirements'
    },
    {
      icon: Bell,
      title: 'Real-time Alerts',
      description: 'Instant notifications when new compliance risks are detected'
    }
  ]

  const regulations = [
    { name: 'HIPAA', color: 'bg-blue-500', desc: 'Healthcare data protection' },
    { name: 'GDPR', color: 'bg-purple-500', desc: 'EU data privacy' },
    { name: 'EU AI Act', color: 'bg-orange-500', desc: 'AI system compliance' },
    { name: 'UAE PDPL', color: 'bg-emerald-500', desc: 'UAE personal data protection' },
    { name: 'CCPA', color: 'bg-red-500', desc: 'California consumer privacy' },
    { name: 'ISO 27001', color: 'bg-cyan-500', desc: 'Information security management' },
    { name: 'SOC 2', color: 'bg-indigo-500', desc: 'Service organization controls' },
    { name: 'Saudi PDPL', color: 'bg-teal-500', desc: 'Saudi personal data protection' },
    { name: 'PCI DSS', color: 'bg-pink-500', desc: 'Payment card security' },
    { name: 'NIST CSF', color: 'bg-amber-500', desc: 'Cybersecurity framework' },
    { name: 'IFRS', color: 'bg-rose-500', desc: 'Financial reporting standards' },
  ]

  const stats = [
    { value: '4-agent', label: 'Review Pipeline' },
    { value: '1,091', label: 'Regulations Covered' },
    { value: '11', label: 'Regulatory Frameworks' },
    { value: 'PDF', label: 'Audit Reports' }
  ]

  const handlePrimaryCta = (source: string) => {
    trackEvent('hero_cta_click', { source, cta: 'upload_contract_for_free' })
    onGetStarted()
  }

  const handleDemoClick = () => {
    trackEvent('demo_cta_click', { source: 'hero' })
    window.location.href = '/sample-compliance-report'
  }

  return (
    <>
    <div className="min-h-screen bg-white overflow-auto">
      {/* Subtle gradient background */}
      <div className="absolute inset-0 bg-gradient-to-b from-brand-50/50 via-white to-white pointer-events-none" />

      <div className="relative z-10">
        {/* Navigation */}
        <nav className="flex items-center justify-between px-6 lg:px-8 py-5 max-w-7xl mx-auto">
          <div
            className="flex items-center gap-3"
          >
            <img src="/auditguard-256.png" alt="" width="48" height="48" className="w-12 h-12 rounded-xl object-contain" />
            <span className="text-xl font-bold text-gray-900">AuditGuard<span className="text-brand-700">.</span></span>
          </div>

          <div
            className="hidden md:flex items-center gap-8"
          >
            <button
              onClick={() => document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth' })}
              className="text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium"
            >
              How it works
            </button>
            <button
              onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
              className="text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium"
            >
              Features
            </button>
            <button
              onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
              className="text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium"
            >
              Pricing
            </button>
            {onContact && (
              <button
                onClick={onContact}
                className="text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium"
              >
                Contact Us
              </button>
            )}
          </div>

          <div
            className="flex items-center gap-3"
          >
            <button
              onClick={onViewDashboard}
              className="px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors font-medium text-sm"
            >
              Log in
            </button>
            <button
              onClick={() => handlePrimaryCta('nav')}
              className="px-5 py-2.5 bg-gray-900 text-white rounded-xl hover:bg-gray-800 transition-colors font-medium text-sm shadow-soft"
            >
              Free Audit
            </button>
          </div>
        </nav>

        <main id="main-content">
        {/* Hero Section */}
        <section className="max-w-7xl mx-auto px-6 lg:px-8 pt-12 pb-20 lg:pt-20 lg:pb-28">
          <div className="text-center max-w-4xl mx-auto">
            {/* Badge */}
            <div
              className="inline-flex items-center gap-2 px-4 py-2 bg-brand-50 border border-brand-200 rounded-full mb-6"
            >
              <span className="w-2 h-2 bg-brand-500 rounded-full animate-pulse" />
              <span className="text-sm text-brand-700 font-medium">AI-Powered Contract Compliance</span>
            </div>

            {/* Social Proof */}
            <div className="flex flex-wrap items-center justify-center gap-3 mb-8">
              <span className="inline-flex items-center gap-2 px-5 py-2.5 bg-orange-50 border border-orange-200 rounded-full text-base lg:text-lg font-bold text-gray-900 shadow-sm">
                <span aria-hidden="true">🔥</span> 100 signups
              </span>
              <span className="inline-flex items-center gap-2 px-5 py-2.5 bg-brand-50 border border-brand-200 rounded-full text-base lg:text-lg font-bold text-brand-700 shadow-sm">
                20 trusted users
              </span>
            </div>

            {/* Main Headline */}
            <h1
              className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight tracking-tight"
            >
              Find missing clauses in your BAA or DPA before legal review.
            </h1>

            {/* Pain Point */}
            <p
              className="text-lg text-gray-500 mb-6 max-w-2xl mx-auto"
            >
              Upload one agreement and receive cited gaps, explanations and suggested wording in minutes.
            </p>

            <div className="bg-white border border-gray-200 rounded-2xl p-5 mb-8 max-w-3xl mx-auto text-left shadow-soft">
              <div className="flex items-center gap-2 mb-4">
                <span className="px-2.5 py-1 bg-red-50 text-red-700 text-xs font-semibold rounded-full">Example finding</span>
                <span className="text-xs text-gray-500">What users see after an audit</span>
              </div>
              <div className="grid gap-4 md:grid-cols-[1fr_auto_1fr] md:items-stretch">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">Risky clause</p>
                  <p className="text-sm text-gray-800 leading-relaxed">"Vendor may use patient data for service improvement and analytics without additional approval."</p>
                </div>
                <div className="hidden md:block w-px bg-gray-200" />
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">AuditGuard result</p>
                  <p className="text-sm text-gray-800 leading-relaxed">
                    Cites <span className="font-semibold text-gray-900">HIPAA 45 CFR §164.502(e)</span> and suggests a replacement clause limiting PHI use to permitted business associate purposes.
                  </p>
                </div>
              </div>
            </div>

            {/* Value Proposition - PROMINENT */}
            <div
              className="bg-gradient-to-r from-brand-50 to-emerald-50 border border-brand-200 rounded-2xl p-6 mb-8 max-w-3xl mx-auto"
            >
              <p className="text-lg lg:text-xl text-gray-800 leading-relaxed font-medium">
                Multi-Agent AI that{' '}
                <span className="text-brand-700 font-semibold">identifies specific clause violations</span> against{' '}
                <span className="font-semibold">HIPAA, GDPR, UAE PDPL, Saudi PDPL, IFRS & 6 more frameworks</span>, then{' '}
                <span className="text-brand-700 font-semibold">drafts suggested replacement language aligned with cited regulatory requirements</span> — every finding cited with a specific regulation article.
              </p>
            </div>

            {/* Supported Regulations */}
            <div
              className="flex flex-wrap items-center justify-center gap-3 mb-8"
            >
              {regulations.map((reg) => (
                <div key={reg.name} className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-full shadow-sm">
                  <div className={`w-2.5 h-2.5 ${reg.color} rounded-full`} />
                  <span className="text-sm font-medium text-gray-700">{reg.name}</span>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
            >
              <button
                onClick={() => handlePrimaryCta('hero')}
                className="flex items-center gap-2 px-8 py-4 bg-brand-700 text-white rounded-xl font-semibold hover:bg-brand-800 transition-all shadow-green hover:shadow-green-lg"
              >
                <Zap className="w-5 h-5" />
                Upload a Contract for Free
              </button>
              <a
                href="/sample-compliance-report"
                onClick={(e) => { e.preventDefault(); handleDemoClick() }}
                className="flex items-center gap-2 px-8 py-4 bg-white border-2 border-gray-200 text-gray-700 rounded-xl font-semibold hover:border-gray-300 hover:bg-gray-50 transition-all"
              >
                <Play className="w-5 h-5" />
                View Demo
              </a>
            </div>
            <p className="mt-3 text-sm text-gray-500">No credit card. First audit is free.</p>
          </div>

          <div className="mt-12 max-w-5xl mx-auto">
            <img
              src="/audit-dashboard-preview.svg"
              alt="AuditGuard compliance audits dashboard showing audit scores, risk categories, and completed reports"
              width="802"
              height="798"
              className="w-full rounded-2xl border border-gray-200 shadow-soft-lg bg-white"
              decoding="async"
            />
          </div>

          {/* Hero Card - How it Works */}
          <div
            id="how-it-works"
            className="mt-16 max-w-4xl mx-auto scroll-mt-24"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-brand-500/20 to-brand-300/20 rounded-3xl blur-3xl transform -rotate-1" />
              <div className="relative bg-white border border-gray-100 rounded-3xl p-8 shadow-soft-lg">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-lg font-semibold text-gray-900">How AuditGuard Works</h2>
                  <span className="flex items-center gap-2 px-3 py-1.5 bg-brand-50 text-brand-700 text-sm rounded-full font-medium">
                    <span className="w-2 h-2 bg-brand-500 rounded-full animate-pulse" />
                    3-Step Process
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {[
                    { step: '01', title: 'Upload BAA or DPA', desc: 'Upload one agreement (PDF, DOCX, or TXT)', icon: FileSearch },
                    { step: '02', title: 'Check Required Clauses', desc: 'Compare its clauses with selected HIPAA or GDPR requirements', icon: BarChart3 },
                    { step: '03', title: 'Review Cited Gaps', desc: 'See explanations, cited requirements, and suggested wording', icon: CheckCircle2 }
                  ].map((item) => (
                    <div
                      key={item.step}
                      className="text-center p-4"
                    >
                      <div className="w-14 h-14 mx-auto bg-brand-50 rounded-2xl flex items-center justify-center mb-4">
                        <item.icon className="w-7 h-7 text-brand-700" />
                      </div>
                      <div className="text-xs text-brand-700 font-bold mb-1">STEP {item.step}</div>
                      <div className="font-semibold text-gray-900 mb-2">{item.title}</div>
                      <div className="text-sm text-gray-500">{item.desc}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="bg-gray-900 py-16 cv-auto">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat) => (
                <div
                  key={stat.label}
                  className="text-center"
                >
                  <div className="text-4xl lg:text-5xl font-bold text-white mb-2">{stat.value}</div>
                  <div className="text-sm text-gray-400 font-medium">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="max-w-7xl mx-auto px-6 lg:px-8 py-24 scroll-mt-20 cv-auto">
          <div
            className="text-center mb-16"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Complete Compliance Solution
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Everything you need to ensure your contracts comply with international regulatory frameworks.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature) => (
              <div
                key={feature.title}
                className="group p-6 bg-white border border-gray-100 rounded-2xl hover:border-brand-200 hover:shadow-soft-lg transition-all duration-300"
              >
                <div className="w-12 h-12 bg-brand-50 rounded-xl flex items-center justify-center mb-4 group-hover:bg-brand-100 group-hover:scale-110 transition-all duration-300">
                  <feature.icon className="w-6 h-6 text-brand-700" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Why Use AuditGuard Section */}
        <section className="bg-brand-50/50 py-24 cv-auto">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div
              className="text-center mb-16"
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-brand-200 rounded-full mb-6">
                <span className="text-sm text-brand-700 font-medium">What makes AuditGuard different</span>
              </div>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">
                Why AuditGuard?
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed text-justify">
                Compliance platforms monitor your servers — they track access controls, automate security questionnaires, and scan your infrastructure.{' '}
                <span className="font-semibold text-gray-900">None of them read your actual contracts.</span>{' '}
                None can tell you that Clause 7.3 of your vendor agreement violates HIPAA §164.312(e)(1) and{' '}
                <span className="text-brand-700 font-semibold">draft replacement language for review</span>.{' '}
                AuditGuard audits contracts at the clause level, cites the exact regulation article, and drafts suggested replacement language for review.{' '}
                That's not a feature difference. <span className="font-semibold text-gray-900">That's a category difference.</span>
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  icon: FileSearch,
                  title: 'Reads Your Actual Contracts',
                  desc: 'We analyze your contract text word by word — every clause checked against regulation requirements. Not infrastructure scans. Not security questionnaires. Your actual legal documents.'
                },
                {
                  icon: Target,
                  title: 'Cites the Exact Regulation Article',
                  desc: 'Every violation references the specific article — HIPAA §164.312, GDPR Article 28, EU AI Act Article 6. No vague "you might have an issue" warnings. Pinpoint citations your regulator will respect.'
                },
                {
                  icon: FileText,
                  title: 'Writes the Fix, Not Just the Flag',
                  desc: 'Other tools tell you something is wrong and leave you to figure it out. AuditGuard drafts suggested replacement language aligned with the cited regulatory requirement.'
                },
                {
                  icon: Brain,
                  title: '4-Agent Verification Pipeline',
                  desc: 'Four specialized AI agents extract, validate, verify, and critique every finding independently. A dedicated Critic Agent catches false positives before they ever reach your report.'
                },
                {
                  icon: Shield,
                  title: '11 Frameworks, One Audit',
                  desc: 'HIPAA, GDPR, EU AI Act, UAE PDPL, CCPA, ISO 27001, SOC 2, Saudi PDPL, PCI DSS, NIST CSF, and IFRS — scanned simultaneously in a single audit. No switching tools, no paying per framework.'
                },
                {
                  icon: Zap,
                  title: 'Results in Minutes, Not Weeks',
                  desc: 'Upload a contract and get a full compliance audit with violations, citations, and fixes — in minutes. No sales calls, no demos, no annual contracts. Start with a free audit today.'
                }
              ].map((item) => (
                <div
                  key={item.title}
                  className="p-6 bg-white rounded-2xl border border-brand-100 shadow-sm hover:shadow-soft-lg hover:border-brand-200 transition-all duration-300"
                >
                  <div className="w-12 h-12 bg-brand-50 rounded-xl flex items-center justify-center mb-4">
                    <item.icon className="w-6 h-6 text-brand-700" />
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>

            {/* Trust Pillars */}
            <div className="grid lg:grid-cols-3 gap-6 mt-12">
              <div className="flex items-start gap-4 p-6 bg-white rounded-2xl border border-brand-100 shadow-sm">
                <div className="w-12 h-12 bg-brand-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Brain className="w-6 h-6 text-brand-700" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Citation-Grounded Review</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">AuditGuard uses a multi-agent critic loop grounded in its regulation database. Findings include specific article citations for human review.</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-6 bg-white rounded-2xl border border-brand-100 shadow-sm">
                <div className="w-12 h-12 bg-brand-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Lock className="w-6 h-6 text-brand-700" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Enterprise Security</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">Your data is isolated, encrypted, and never used for AI training. Private API layers ensure nothing is exposed publicly.</p>
                </div>
              </div>
              <div className="flex items-start gap-4 p-6 bg-white rounded-2xl border border-brand-100 shadow-sm">
                <div className="w-12 h-12 bg-brand-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <DollarSign className="w-6 h-6 text-brand-700" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">Review-Ready Reports</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">One-click PDF audit reports with cited findings, risk summaries, and suggested remediation language for counsel, compliance teams, and auditors to review.</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Pricing Section */}
        <section id="pricing" className="max-w-7xl mx-auto px-6 lg:px-8 py-24 scroll-mt-20 cv-auto">
          <div
            className="text-center mb-10"
          >
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto mb-2">
              Start with one limited audit, buy a single full review, or choose a monthly plan for regular document volume.
            </p>
            <p className="text-sm text-gray-500">No credit card for the free audit · 7-day trial on Professional and Team · Purchased credits roll over until used</p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {PLANS.map((plan) => {
              const displayPrice = plan.priceDisplay
              const isFree = plan.id === 'free'
              const isDark = false
              return (
                <div
                  key={plan.id}
                  className={`relative rounded-2xl p-8 hover:shadow-soft-lg transition-all duration-300 ${
                    plan.highlighted
                      ? 'bg-white border-2 border-brand-500'
                      : isDark
                        ? 'bg-gray-900 text-white'
                        : 'bg-white border border-gray-200'
                  }`}
                >
                  {plan.badge && (
                    <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                      <span className="px-4 py-1 bg-brand-700 text-white text-xs font-bold rounded-full uppercase tracking-wide">
                        {plan.badge}
                      </span>
                    </div>
                  )}
                  <div className="mb-6">
                    <h3 className={`text-lg font-semibold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>{plan.name}</h3>
                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{plan.description}</p>
                  </div>
                  <div className="flex items-baseline gap-1 mb-1">
                    <span className={`text-5xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{displayPrice}</span>
                    {!isFree && <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{plan.periodLabel || '/mo'}</span>}
                  </div>
                  {plan.tagline ? (
                    <p className={`text-sm font-medium mb-5 ${isDark ? 'text-brand-400' : 'text-brand-700'}`}>{plan.tagline}</p>
                  ) : <div className="mb-5" />}
                  <button
                    onClick={() => {
                      trackEvent('pricing_cta_click', { plan: plan.id, cta: plan.cta })
                      const action = plan.id === 'free' ? onGetStarted : (onCheckout || onGetStarted)
                      action()
                    }}
                    className={`w-full py-3 px-6 rounded-xl font-semibold transition-all mb-8 ${
                      plan.highlighted
                        ? 'bg-brand-700 text-white hover:bg-brand-800 shadow-green'
                        : isDark
                          ? 'bg-brand-700 text-white hover:bg-brand-800 shadow-green'
                          : 'border-2 border-brand-700 text-brand-700 hover:bg-brand-50'
                    }`}
                  >
                    {plan.cta}
                  </button>
                  <ul className="space-y-3">
                    {plan.features.map((feature) => (
                      <li key={feature} className={`flex items-start gap-3 text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        <CheckCircle2 className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isDark ? 'text-brand-400' : 'text-brand-500'}`} />
                        {feature}
                      </li>
                    ))}
                    {plan.disabledFeatures?.map((feature) => (
                      <li key={feature} className="flex items-start gap-3 text-sm text-gray-400">
                        <XCircle className="w-5 h-5 text-gray-300 flex-shrink-0 mt-0.5" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              )
            })}
          </div>

          {/* Enterprise */}
          <div className="max-w-6xl mx-auto mt-8">
            <div
              className="bg-gray-900 text-white rounded-2xl p-8"
            >
              <h3 className="text-lg font-semibold mb-1">{ENTERPRISE.name}</h3>
              <p className="text-sm text-gray-400 mb-4">{ENTERPRISE.description}</p>
              <div className="flex items-baseline gap-1 mb-5">
                <span className="text-4xl font-bold">{ENTERPRISE.priceDisplay}</span>
              </div>
              <a
                href="mailto:info@auditguard.org?subject=Enterprise%20Inquiry"
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-gray-900 rounded-xl font-semibold hover:bg-gray-100 transition-all mb-5"
              >
                {ENTERPRISE.cta}
                <ArrowRight className="w-4 h-4" />
              </a>
              <ul className="space-y-2 border-t border-white/10 pt-4">
                {ENTERPRISE.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-xs text-gray-300">
                    <CheckCircle2 className="w-4 h-4 text-brand-400 flex-shrink-0 mt-0.5" />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* FAQ strip */}
          <div className="max-w-3xl mx-auto mt-16">
            <h3 className="text-center text-sm font-semibold text-gray-500 uppercase tracking-wide mb-6">Common questions</h3>
            <div className="space-y-4">
              <div className="bg-white border border-gray-200 rounded-xl p-5">
                <p className="font-semibold text-gray-900 mb-1">Why not just use ChatGPT, Gemini, or Claude?</p>
                <p className="text-sm text-gray-600">ChatGPT, Gemini, or Claude? reads text. AuditGuard cites <span className="font-medium">1,073 specific regulation articles</span>, produces audit-trail PDFs your DPO can show a regulator, redacts PII before analysis, and generates remediation text mapped to exact article IDs. An auditor won't accept a ChatGPT screenshot.</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-5">
                <p className="font-semibold text-gray-900 mb-1">Do I need a credit card for the free audit?</p>
                <p className="text-sm text-gray-600">No. Run one limited audit without a card, then pay only if you need a full report or additional audits.</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-5">
                <p className="font-semibold text-gray-900 mb-1">What if I only need one audit?</p>
                <p className="text-sm text-gray-600">Buy one full document audit for <span className="font-medium">$39</span>. The purchased credit remains available until you use it.</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-5">
                <p className="font-semibold text-gray-900 mb-1">How does AuditGuard compare to Vanta or Drata?</p>
                <p className="text-sm text-gray-600">Vanta and Drata monitor <span className="font-medium">infrastructure</span> (servers, access). AuditGuard reads your <span className="font-medium">actual contracts and policies</span> and cites specific regulation violations. Different jobs — many customers use both.</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="max-w-7xl mx-auto px-6 lg:px-8 py-24 cv-auto">
          <div
            className="bg-gray-900 rounded-3xl p-12 lg:p-16 text-center relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-brand-500/20 to-transparent pointer-events-none" />
            <div className="relative z-10">
              <Shield className="w-14 h-14 text-brand-400 mx-auto mb-6" />
              <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
                Stop Paying $500/hr for Manual Audits
              </h2>
              <p className="text-gray-400 mb-8 max-w-xl mx-auto">
                Get clause-level compliance analysis against HIPAA, GDPR, UAE PDPL, Saudi PDPL, IFRS & 6 more frameworks in seconds, not days.
              </p>
              <button
                onClick={() => handlePrimaryCta('bottom_cta')}
                className="inline-flex items-center gap-2 px-8 py-4 bg-brand-700 text-white rounded-xl font-semibold hover:bg-brand-800 transition-all shadow-green-lg"
              >
                <CheckCircle2 className="w-5 h-5" />
                Upload a Contract for Free
                <ArrowRight className="w-5 h-5" />
              </button>
              <p className="mt-5 text-xs text-gray-500 max-w-2xl mx-auto">
                AuditGuard AI is not a law firm and does not provide legal advice. Outputs should be reviewed by qualified counsel before use.
              </p>
            </div>
          </div>
        </section>
        </main>

        {/* Footer */}
        <footer className="border-t border-gray-100 py-12 bg-white cv-auto">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <img src="/auditguard-48.png" alt="" width="32" height="32" className="w-8 h-8 rounded-lg object-contain" />
                <span className="text-gray-900 font-semibold">AuditGuard<span className="text-brand-700">.</span></span>
              </div>
              <div className="flex items-center gap-4 flex-wrap">
                {onContact && (
                  <a
                    href="/contact"
                    onClick={(e) => { e.preventDefault(); onContact() }}
                    className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    Contact Us
                  </a>
                )}
                {onTerms && (
                  <a
                    href="/terms"
                    onClick={(e) => { e.preventDefault(); onTerms() }}
                    className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    Terms of Service
                  </a>
                )}
                {onPrivacy && (
                  <a
                    href="/privacy"
                    onClick={(e) => { e.preventDefault(); onPrivacy() }}
                    className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    Privacy Policy
                  </a>
                )}
                {onRefundPolicy && (
                  <a
                    href="/refund-policy"
                    onClick={(e) => { e.preventDefault(); onRefundPolicy() }}
                    className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    Refund Policy
                  </a>
                )}
                {onCookiePolicy && (
                  <a
                    href="/cookie-policy"
                    onClick={(e) => { e.preventDefault(); onCookiePolicy() }}
                    className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    Cookie Policy
                  </a>
                )}
                <a
                  href="/security"
                  className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
                >
                  Security
                </a>
                <a
                  href="/sample-compliance-report"
                  className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
                >
                  Sample Report
                </a>
                <p className="text-sm text-gray-500">
                  &copy; 2026 AuditGuard AI, owned and operated by Muaz Ashraf.
                </p>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>

  {/* Demo Video Modal */}
    {showDemo && (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
        onClick={() => setShowDemo(false)}
      >
        <div
          className="relative w-full max-w-5xl"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={() => setShowDemo(false)}
            className="absolute -top-12 right-0 text-white/80 hover:text-white transition-colors flex items-center gap-2 text-sm"
          >
            Close <X className="w-5 h-5" />
          </button>
          <div className="rounded-2xl overflow-hidden shadow-2xl bg-black">
            <video
              ref={videoRef}
              src={DEMO_VIDEO_URL}
              controls
              autoPlay
              className="w-full"
              playsInline
            />
          </div>
        </div>
      </div>
    )}
  </>
  )
}

export default LandingPage
